import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ThreeMonthsExtension from "../Customer_Registered_Product/ThreeMonthsExtension";
import ProductService from "../Customer_Registered_Product/ProductService";
import AWR from "../AdvanceWarrantyReplacement/AWR"
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from '../../ApiConfig/ApiConfig';
import Cookies from "js-cookie";

const CustomerProductDetails = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showWarrantyColumn, setShowWarrantyColumn] = useState(false);
  const [showWarrantyExtensionDetails, setShowWarrantyExtensionDetails] = useState(null);
  const [showProductServiceDetails, setShowProductServiceDetails] = useState(null);
  const [showAWRDetails, setShowAWRDetails] = useState(null);
  const token = Cookies.get("token");

  // Product types that should hide the AWR button
  const hideAWRProductTypes = ['CAT-6', 'TELEPHONE', 'LIFT', 'HDMI', 'CCTV', 'POWER SUPPLY'];

  const fetchProducts = useCallback(async (customerId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/Registered/fetch?customerId=${customerId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          }
        }
      );
      const fetchedProducts = response.data;

      if (fetchedProducts.length > 0) {
        const lastProduct = fetchedProducts[fetchedProducts.length - 1];
        const remainingProducts = fetchedProducts.slice(0, -1);
        const reorderedProducts = [lastProduct, ...remainingProducts];

        setProducts(reorderedProducts);

        const isAnyProductEligible = reorderedProducts.some((product) =>
          isWarrantyExtensionEligible(product.purchaseDate)
        );
        setShowWarrantyColumn(isAnyProductEligible);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const customerId = decodedToken.customerId;
        fetchProducts(customerId);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    } else {
      console.warn("No token found in Cookies.");
    }
  }, [fetchProducts]);

  const isWarrantyExtensionEligible = (purchaseDate) => {
    const today = new Date();
    const purchaseDateObj = new Date(purchaseDate);
    const sevenDaysFromPurchase = new Date(
      purchaseDateObj.setDate(purchaseDateObj.getDate() + 7)
    );
    return today <= sevenDaysFromPurchase;
  };

  const handleRowClick = (product) => {
    if (selectedProduct && selectedProduct._id === product._id) {
      setSelectedProduct(null);
    } else {
      setSelectedProduct(product);
      setShowWarrantyExtensionDetails(null);
    }
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleShowWarrantyExtensionDetails = (product) => {
    setShowWarrantyExtensionDetails(product);
  };

  const handleShowProductServiceDetails = (product) => {
    setShowProductServiceDetails(product);
  };

  const handleShowAWRDetails = (product) => {
    setShowAWRDetails(product);
  };

  const handleCloseWarrantyExtensionDetails = () => {
    setShowWarrantyExtensionDetails(null);
  };

  const handleCloseProductServiceDetails = () => {
    setShowProductServiceDetails(null);
  };

  const handleCloseAWRDetails = () => {
    setShowAWRDetails(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm sm:shadow-md overflow-hidden" 
         style={{ boxShadow: '0px 10px 60px 0px #E2ECF980' }}>
      
      {/* Header Section */}
      <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-700 mb-1">
          Your Product Details
        </h1>
        <p className="text-sm text-gray-500 hidden sm:block">
          Manage your registered products and services
        </p>
      </div>

      {products.length > 0 ? (
        <>
          {/* Desktop Table - Hidden on mobile and tablet */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full bg-white border-collapse">
              <thead className="bg-[#1C50A3] text-white">
                <tr>
                  <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                    Product Name
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                    Product ID
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                    Product Expiry
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                    Product Service
                  </th>
                  <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                    AWR
                  </th>
                  {showWarrantyColumn && (
                    <th className="py-3 px-4 xl:px-6 border-b font-semibold text-left text-sm whitespace-nowrap">
                      Extend Warranty
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr
                    key={product._id}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                    }`}
                    onClick={() => handleRowClick(product)}
                  >
                    <td className="py-3 px-4 xl:px-6 border-b text-left text-gray-700 text-sm font-medium">
                      {product.productName}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b text-left text-gray-600 text-sm">
                      {product.productID}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b text-left text-gray-600 text-sm">
                      {new Date(product.expiryDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b text-left">
                      <button
                        className="bg-[#1C50A3] text-white font-medium px-4 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowProductServiceDetails(product);
                        }}
                      >
                        Apply
                      </button>
                    </td>
                    <td className="py-3 px-4 xl:px-6 border-b text-left">
                      {!hideAWRProductTypes.includes(product.productType) && !product.isClaimed ? (
                        <button
                          className="bg-[#1C50A3] text-white font-medium px-4 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm whitespace-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowAWRDetails(product);
                          }}
                        >
                          Apply
                        </button>
                      ) : (
                        <span className="text-gray-500 font-medium text-sm">Not Applicable</span>
                      )}
                    </td>
                    {showWarrantyColumn && (
                      <td className="py-3 px-4 xl:px-6 border-b text-left">
                        {isWarrantyExtensionEligible(product.purchaseDate) ? (
                          <button
                            className="bg-[#1C50A3] text-white font-medium px-4 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowWarrantyExtensionDetails(product);
                            }}
                          >
                            Apply
                          </button>
                        ) : (
                          <span className="text-gray-500 font-medium text-sm">Not Applicable</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tablet View - Card Layout */}
          <div className="hidden md:block lg:hidden">
            <div className="p-4 space-y-4">
              {products.map((product, index) => (
                <div
                  key={product._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleRowClick(product)}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{product.productName}</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Product ID:</span>
                          <span className="text-gray-700">{product.productID}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Expiry:</span>
                          <span className="text-gray-700">
                            {new Date(product.expiryDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Service:</span>
                        <button
                          className="bg-[#1C50A3] text-white font-medium px-3 py-1 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowProductServiceDetails(product);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                      
                      {!hideAWRProductTypes.includes(product.productType) && !product.isClaimed && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">AWR:</span>
                          <button
                            className="bg-[#1C50A3] text-white font-medium px-3 py-1 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowAWRDetails(product);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                      
                      {showWarrantyColumn && isWarrantyExtensionEligible(product.purchaseDate) && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Warranty:</span>
                          <button
                            className="bg-[#1C50A3] text-white font-medium px-3 py-1 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowWarrantyExtensionDetails(product);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile View - Stacked Cards */}
          <div className="md:hidden">
            <div className="p-3 space-y-3">
              {products.map((product, index) => (
                <div
                  key={product._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => handleRowClick(product)}
                >
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base mb-2 leading-tight">
                        {product.productName}
                      </h3>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 font-medium">Product ID:</span>
                        <span className="text-gray-800 text-right">{product.productID}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 font-medium">Expiry:</span>
                        <span className="text-gray-800 text-right">
                          {new Date(product.expiryDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium text-sm">Product Service:</span>
                        <button
                          className="bg-[#1C50A3] text-white font-medium px-3 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowProductServiceDetails(product);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                      
                      {!hideAWRProductTypes.includes(product.productType) && !product.isClaimed && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium text-sm">AWR:</span>
                          <button
                            className="bg-[#1C50A3] text-white font-medium px-3 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowAWRDetails(product);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                      
                      {showWarrantyColumn && isWarrantyExtensionEligible(product.purchaseDate) && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium text-sm">Extend Warranty:</span>
                          <button
                            className="bg-[#1C50A3] text-white font-medium px-3 py-1.5 rounded-full hover:bg-[#144080] transition-all duration-200 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowWarrantyExtensionDetails(product);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="p-8 sm:p-12 text-center">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8V4a1 1 0 00-1-1H7a1 1 0 00-1 1v1M9 7h6M9 11h6" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
            <p className="text-gray-600 text-sm">You haven't registered any products yet.</p>
          </div>
        </div>
      )}

      {/* Responsive Modals */}
      {showWarrantyExtensionDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <ThreeMonthsExtension
              product={showWarrantyExtensionDetails}
              onClose={handleCloseWarrantyExtensionDetails}
            />
          </div>
        </div>
      )}

      {showProductServiceDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <ProductService
              product={showProductServiceDetails}
              onClose={handleCloseProductServiceDetails}
            />
          </div>
        </div>
      )}

      {showAWRDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <AWR
              product={showAWRDetails}
              onClose={handleCloseAWRDetails}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProductDetails;
